import { useApolloClient } from '@apollo/react-hooks'
import { GET_DATA } from '@components/Modal/SingleProfileModal/QuerySingleProfile'
import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import { InstructorGroupInfoType, GroupClassListItem } from '@interfaces/MySchedule'
import QueryResult from '@interfaces/QueryResult'
import { GET_LESSON_GROUPS } from '@queries/index'
import { removeOrientation } from '@utils/helpers/sessions'
import find from 'lodash/find'
import first from 'lodash/first'
import flatten from 'lodash/flatten'
import get from 'lodash/get'
import includes from 'lodash/includes'
import last from 'lodash/last'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'
import React from 'react'
import { Query } from 'react-apollo'
import { DATE_FORMAT } from '@utils/constants'
import { StudentProfileInfo } from '@interfaces/Student'

export interface QueryPageResults {
  loading: boolean
  data: InstructorGroupInfoType
}

export interface QueryPageDataProps {
  InstructorId: string
  StartDateTime: string
  EndDateTime: string
  children: (results: QueryPageResults) => {}
}

export type Props = QueryPageDataProps & WithLessonUtilsProp

export interface QueryStudentsResults {
  loading: boolean
  data: any
}

export interface QueryStudentsProps {
  groups: GroupClassListItem[]
  children: (results: QueryStudentsResults) => {}
}

const QueryStudents: React.FC<QueryStudentsProps> = ({ groups, children }) => {
  const client = useApolloClient()
  const [loading, setLoading] = React.useState(false)
  const [students, setStudents] = React.useState<any>([])

  React.useEffect(() => {
    const init = async () => {
      setLoading(true)
      const promisedStudents = groups
        // remove null studentIds
        .filter(group => group?.SessionInfoList?.[0]?.CustomerId || group?.StudentGroupId)
        .map(async group => {
          const StudentId = group?.SessionInfoList?.[0]?.CustomerId || group?.StudentGroupId
          const params = {
            StudentId,
            Language: group.Language,
          }

          const res = await client.query<any>({
            query: GET_DATA,
            variables: params,
          })

          const student = (res.data?.getStudents?.[0]?.StudentProfileInfo ?? {}) as StudentProfileInfo
          return {
            ...student,
            StudentId,
          }
        })

      let students = await Promise.all(promisedStudents)
      setStudents(students)
      setLoading(false)
    }

    init()
    /**
     * https://stackoverflow.com/questions/59467758/passing-array-to-useeffect-dependency-list
     */
  }, [JSON.stringify(groups)])

  return (
    <>
      {children({
        loading,
        data: students,
      })}
    </>
  )
}

const QueryPageData: React.FC<Props> = ({ InstructorId, StartDateTime, EndDateTime, children, ...props }) => {
  const params = {
    InstructorId,
    StartDateTime,
    EndDateTime,
    FILTER_TYPE: 'Groups',
  }

  return (
    <Query query={GET_LESSON_GROUPS} variables={{ params }} fetchPolicy="network-only">
      {({ loading: groupLoading, data }: QueryResult) => {
        let result: InstructorGroupInfoType = get(data, 'getLessonGroups.0.InstructorGroupInfo', {})
        let groups = props.onlyValidLessons(get(result, 'GroupClassList', []))

        const state: GroupClassListItem[] = []
        groups = reduce(
          groups,
          (accumulator, group) => {
            if (group.ProgramType === 'Individual' || group.Type === 'Gap Filler') {
              const StartTime = get(group, 'StartTime', '')
              const EndTime = get(group, 'EndTime', '')

              const metadata = props.getSessionMetadata(result.GroupClassList, group)

              const SessionMetaData = {
                StartTime: metadata.StartTime,
                EndTime: metadata.EndTime,
                Index: metadata.Index,
              }

              const Type = group.Type === 'Gap Filler' ? group.GapFiller : group.Type

              accumulator = accumulator.concat({
                ...group,
                StartTime,
                EndTime,
                Type,
                SessionMetaData,
              })
            } else {
              const isGroupExist = find(
                accumulator,
                acc => acc.SessionId === group.SessionId && includes(acc.ProgramType, 'Group')
              )
              let instances = result.GroupClassList.filter(
                flesson => flesson.SessionId === group.SessionId && includes(flesson.ProgramType, 'Group')
              )
              if (instances.length && !isGroupExist) {
                instances = sortBy(instances, instance => moment.utc(instance.StartTime, DATE_FORMAT.DT).toDate())
                const allSessions = uniqBy(flatten(map(instances, 'SessionInfoList')), 'CustomerId')
                const StartTime = get(first(instances), 'StartTime', '')
                const EndTime = get(last(instances), 'EndTime', '')

                let lastLesson = last(instances)
                if (lastLesson) {
                  const index = instances.map(item => item.Id).indexOf(lastLesson?.Id)
                  const SessionMetaData = {
                    Index: index + 1,
                    StartTime: '',
                    EndTime: '',
                  }

                  lastLesson = {
                    ...lastLesson,
                    SessionMetaData,
                  }
                }

                const SessionMetaData = {
                  StartTime,
                  EndTime,
                  Index: 1,
                  CounsellingLesson: lastLesson,
                  GroupMaterials: map(instances, ({ StartTime, EndTime, MaterialList, LastMaterialList }) => ({
                    StartTime,
                    EndTime,
                    MaterialList,
                    LastMaterialList,
                  })),
                  GroupNotes: map(instances, ({ StartTime, EndTime, SessionInfoList }) => ({
                    StartTime,
                    EndTime,
                    Notes: [],
                    SessionIds: map(SessionInfoList, 'Id'),
                  })),
                }

                const Type = group.Type === 'Gap Filler' ? group.GapFiller : group.Type
                const GroupName =
                  allSessions.length > 1 ? `${group.GroupName} (${allSessions.length} students)` : group.GroupName

                accumulator = accumulator.concat({
                  ...group,
                  StartTime,
                  EndTime,
                  Type,
                  SessionMetaData,
                  GroupName,
                  Status: 'Done',
                })
              }
            }

            return accumulator
          },
          state
        )
        groups = sortBy(groups, group => moment.utc(group.StartTime, 'YYYY-MM-DD HH:mm:ss').toDate()).map(group =>
          Object.assign({}, group, { ZoomUsername: result.ZoomUsername, VirtualClassRoomId: group.VirtualClassRoomId })
        )
        return (
          <QueryStudents groups={groups}>
            {({ loading: studentLoading, data: students }) => {
              const temp = () =>
                map(groups, group => {
                  const StudentId = group?.SessionInfoList?.[0]?.CustomerId
                  const program = (students || []).reduce((accumulator, student) => {
                    if (student.StudentId === StudentId) {
                      const program = student.ProgramStudentWrappers.find(
                        pw => pw.ProgramStudentInfo.ProgramId === group.ProgramId
                      )
                      if (program) {
                        accumulator = program
                      }
                    }

                    return accumulator
                  }, undefined)

                  return {
                    ...group,
                    TotalLessons: program?.ProgramStudentInfo?.TotalLessons ?? 0,
                    LessonsTaken: program?.ProgramStudentInfo?.LessonsTaken ?? 0,
                    LessonsRemaining: program?.ProgramStudentInfo?.LessonsRemaining ?? 0,
                    StudentProfile: students.find(student => student?.StudentId === StudentId),
                  }
                })

              const loading = groupLoading || studentLoading
              return children({
                loading,
                data: {
                  ...result,
                  GroupClassList: removeOrientation(temp()),
                },
              })
            }}
          </QueryStudents>
        )
      }}
    </Query>
  )
}

export default WithLessonUtilsHOC(QueryPageData)
